/**
 * Item Price Actions Modal Component
 * 
 * Modal for managing Item Price actions - edit and delete.
 * Similar to StockActionsModal but for Item Price management.
 */

import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ArrowLeft, Trash2, Save } from "lucide-react";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import { frappeMethod } from "@/lib/erp/utils";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LinkFieldSearch } from "@/components/filter-builder/LinkFieldSearch";
import { MODAL_INPUT_CONSTRAINED_CLASSES, MODAL_LINK_FIELD_SEARCH_CLASSES } from "@/lib/styles/modal";
import type { ItemPrice } from "@/lib/erp";

type ModalScreen = "menu" | "edit";

interface ItemPriceActionsModalState {
  screen: ModalScreen;
}

interface ItemPriceActionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemPrice: ItemPrice | null;
  itemCode: string;
}

export function ItemPriceActionsModal({
  open,
  onOpenChange,
  itemPrice,
}: ItemPriceActionsModalProps) {
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  
  const ui = useMemo(() => {
    const t = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      itemPriceActions: t("item_price_actions", "Item Price Actions"),
      editItemPrice: t("edit_item_price", "Edit Item Price"),
      manageItemPrice: t("manage_item_price", "Manage item price"),
      editPriceDescription: t("edit_price_list_rate_dates_packing", "Edit price list, rate, dates, and packing unit"),
      permanentlyDelete: t("permanently_delete_item_price", "Permanently delete this item price"),
      priceList: t("price_list", "Price List"),
      rate: t("rate", "Rate"),
      validFrom: t("valid_from", "Valid From"),
      validTo: t("valid_to", "Valid To"),
      packingUnit: t("packing_unit", "Packing Unit"),
      priceDetails: t("price_details", "Price Details"),
      validityPeriod: t("validity_period", "Validity Period"),
      packingInformation: t("packing_information", "Packing Information"),
      selectPriceList: t("select_price_list", "Select price list..."),
      delete: t("delete", "Delete"),
      save: t("save", "Save"),
      cancel: t("cancel", "Cancel"),
      deleteConfirm: t("delete_confirm_item_price", "Are you sure you want to delete this item price?"),
      saving: t("saving", "Saving..."),
      deleting: t("deleting", "Deleting..."),
      saved: t("saved", "Saved"),
      deleted: t("deleted", "Deleted"),
      error: t("error", "Error"),
    };
  }, [language]);

  // Modal state machine
  const [state, setState] = useState<ItemPriceActionsModalState>({ screen: "menu" });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    price_list: "",
    price_list_rate: "",
    valid_from: "",
    valid_upto: "",
    packing_unit: "",
  });

  // Reset to menu when modal closes
  useEffect(() => {
    if (!open) {
      setState({ screen: "menu" });
      setError(null);
    }
  }, [open]);

  // Initialize form data when itemPrice changes
  useEffect(() => {
    if (itemPrice) {
      try {
        const formatDate = (date: string | null | undefined): string => {
          if (!date) return "";
          try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return "";
            return dateObj.toISOString().split('T')[0];
          } catch {
            return "";
          }
        };

        setFormData({
          price_list: itemPrice.price_list || "",
          price_list_rate: itemPrice.price_list_rate?.toString() || "",
          valid_from: formatDate(itemPrice.valid_from),
          valid_upto: formatDate(itemPrice.valid_upto),
          packing_unit: itemPrice.packing_unit?.toString() || "",
        });
      } catch (error) {
        console.error("[ItemPriceActionsModal] Error initializing form data:", error);
        setFormData({
          price_list: "",
          price_list_rate: "",
          valid_from: "",
          valid_upto: "",
          packing_unit: "",
        });
      }
    }
  }, [itemPrice]);

  // Dynamic modal title based on screen
  const modalTitle = useMemo(() => {
    if (state.screen === "menu") return ui.itemPriceActions;
    if (state.screen === "edit") return ui.editItemPrice;
    return ui.itemPriceActions;
  }, [state.screen, ui]);

  // Handle edit action
  const handleEditClick = () => {
    setState({ screen: "edit" });
  };

  // Handle back navigation
  const handleBack = () => {
    setState({ screen: "menu" });
    setError(null);
  };

  // Handle save
  const handleSave = async () => {
    if (!itemPrice) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      // frappe.client.set_value expects: doctype, name, fieldname (dict of field-value pairs)
      const payload = {
        doctype: "Item Price",
        name: itemPrice.name,
        fieldname: {
          price_list: formData.price_list || null,
          price_list_rate: formData.price_list_rate ? parseFloat(formData.price_list_rate) : null,
          valid_from: formData.valid_from || null,
          valid_upto: formData.valid_upto || null,
          packing_unit: formData.packing_unit ? parseFloat(formData.packing_unit) : null,
        },
      };

      await frappeMethod("frappe.client.set_value", payload);
      
      // Invalidate queries to refresh data
      // Note: useDocTypeListWithPagination uses ["doctype-list", doctype, ...] as query key
      queryClient.invalidateQueries({ queryKey: ["doctype-list", "Item Price"] });
      
      // Close modal
      onOpenChange(false);
    } catch (err: any) {
      console.error("[ItemPriceActionsModal] Failed to save:", err);
      setError(err.message || ui.error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!itemPrice) return;
    
    if (!confirm(ui.deleteConfirm)) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await frappeMethod("frappe.client.delete", {
        doctype: "Item Price",
        name: itemPrice.name,
      });
      
      // Invalidate queries to refresh data
      // Note: useDocTypeListWithPagination uses ["doctype-list", doctype, ...] as query key
      queryClient.invalidateQueries({ queryKey: ["doctype-list", "Item Price"] });
      
      // Close modal
      onOpenChange(false);
    } catch (err: any) {
      console.error("[ItemPriceActionsModal] Failed to delete:", err);
      setError(err.message || ui.error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Don't render if no itemPrice
  if (!itemPrice) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(open) => {
      onOpenChange(open);
      // Reset state when modal closes
      if (!open) {
        setState({ screen: "menu" });
        setError(null);
      }
    }}>
      <DialogContent className="max-w-[680px] max-h-[85vh] bg-white dark:bg-gray-900 p-0 flex flex-col overflow-hidden">
        {/* Top fade-out gradient */}
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
        
        <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
          <DialogHeader className="px-10 pt-20 pb-4 text-center">
            {/* Back button - only show on edit screen */}
            {state.screen !== "menu" && (
              <div className="absolute top-6 left-6">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="h-10 w-10 rounded-full"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </div>
            )}

            <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px] mb-2">
              {modalTitle}
            </DialogTitle>
            <DialogDescription className="text-[15px] font-normal text-gray-600 dark:text-gray-400 leading-relaxed">
              {state.screen === "menu" 
                ? ui.manageItemPrice
                : `${ui.editItemPrice} - ${itemPrice?.price_list || itemPrice?.name || ""}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className={cn(
            "pb-10 px-10",
            state.screen === "edit" ? "pt-0 p-0 flex flex-col flex-1 min-h-0" : "pt-4"
          )}>
            {/* Menu Screen */}
            {state.screen === "menu" && (
              <div className="space-y-4">
                {/* Edit Action */}
                <button
                  type="button"
                  onClick={handleEditClick}
                  className={cn(
                    "w-full flex items-center gap-5 min-h-[60px] px-6 py-4",
                    "bg-gray-50 dark:bg-gray-950 rounded-lg border border-gray-200/50 dark:border-gray-700/50 shadow-sm",
                    "hover:bg-gray-100/80 dark:hover:bg-gray-900/60 transition-all duration-200 ease-out",
                    "active:bg-gray-100 dark:active:bg-gray-900/70"
                  )}
                >
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <Save className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-[17px] font-normal text-gray-900 dark:text-gray-100 leading-[1.35]">
                      {ui.editItemPrice}
                    </div>
                    <div className="text-[15px] text-gray-500 dark:text-gray-400 mt-1 leading-[1.4]">
                      {ui.editPriceDescription}
                    </div>
                  </div>
                </button>

                {/* Delete Action */}
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className={cn(
                    "w-full flex items-center gap-5 min-h-[60px] px-6 py-4",
                    "bg-gray-50 dark:bg-gray-950 rounded-lg border border-red-200/50 dark:border-red-800/50 shadow-sm",
                    "hover:bg-red-50/80 dark:hover:bg-red-950/60 transition-all duration-200 ease-out",
                    "active:bg-red-50 dark:active:bg-red-950/70",
                    "disabled:opacity-40 disabled:cursor-not-allowed"
                  )}
                >
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-[17px] font-normal text-red-600 dark:text-red-400 leading-[1.35]">
                      {ui.delete}
                    </div>
                    <div className="text-[15px] text-red-500 dark:text-red-500 mt-1 leading-[1.4]">
                      {isDeleting ? ui.deleting : ui.permanentlyDelete}
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* Edit Screen */}
            {state.screen === "edit" && itemPrice && (
              <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
                  <div className="pb-8 px-10 pt-2">
                    {error && (
                      <Alert variant="destructive" className="mb-6">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <div className="py-2 space-y-5">
                      {/* Price Details Group */}
                      <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                        <div className="px-5 py-2.5 border-b border-gray-200/30 dark:border-gray-700/30">
                          <span className="text-[13px] font-medium text-gray-600 dark:text-gray-400 tracking-[-0.1px]">
                            {ui.priceDetails}
                          </span>
                        </div>
                        
                        {/* Price List */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="price_list" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.priceList}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <LinkFieldSearch
                              doctype="Price List"
                              referenceDoctype="Item Price"
                              value={formData.price_list || null}
                              onChange={(value) => setFormData({ ...formData, price_list: value || "" })}
                              placeholder={ui.selectPriceList}
                              className={cn("flex-1", MODAL_LINK_FIELD_SEARCH_CLASSES, "transition-none [border-image:none]")}
                              data-field="price_list"
                              align="end"
                            />
                          </div>
                        </div>

                        {/* Rate */}
                        <div className="flex items-center justify-between min-h-[52px] px-5">
                          <label htmlFor="price_list_rate" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.rate}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <Input
                              id="price_list_rate"
                              type="number"
                              step="0.01"
                              value={formData.price_list_rate}
                              onChange={(e) => setFormData({ ...formData, price_list_rate: e.target.value })}
                              placeholder="0.00"
                              className={cn(MODAL_INPUT_CONSTRAINED_CLASSES, "transition-none")}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Validity Dates Group */}
                      <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                        <div className="px-5 py-2.5 border-b border-gray-200/30 dark:border-gray-700/30">
                          <span className="text-[13px] font-medium text-gray-600 dark:text-gray-400 tracking-[-0.1px]">
                            {ui.validityPeriod}
                          </span>
                        </div>
                        
                        {/* Valid From */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label htmlFor="valid_from" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.validFrom}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <Input
                              id="valid_from"
                              type="date"
                              value={formData.valid_from}
                              onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                              className={cn(MODAL_INPUT_CONSTRAINED_CLASSES, "transition-none [border-image:none]")}
                            />
                          </div>
                        </div>

                        {/* Valid To */}
                        <div className="flex items-center justify-between min-h-[52px] px-5">
                          <label htmlFor="valid_upto" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.validTo}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <Input
                              id="valid_upto"
                              type="date"
                              value={formData.valid_upto}
                              onChange={(e) => setFormData({ ...formData, valid_upto: e.target.value })}
                              className={cn(MODAL_INPUT_CONSTRAINED_CLASSES, "transition-none")}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Packing Unit Group */}
                      <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                        <div className="px-5 py-2.5 border-b border-gray-200/30 dark:border-gray-700/30">
                          <span className="text-[13px] font-medium text-gray-600 dark:text-gray-400 tracking-[-0.1px]">
                            {ui.packingInformation}
                          </span>
                        </div>
                        
                        {/* Packing Unit */}
                        <div className="flex items-center justify-between min-h-[52px] px-5">
                          <label htmlFor="packing_unit" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                            {ui.packingUnit}
                          </label>
                          <div className="flex-1 max-w-[60%] flex justify-end">
                            <Input
                              id="packing_unit"
                              type="number"
                              step="1"
                              value={formData.packing_unit}
                              onChange={(e) => setFormData({ ...formData, packing_unit: e.target.value })}
                              placeholder="0"
                              className={cn(MODAL_INPUT_CONSTRAINED_CLASSES, "transition-none")}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom fade-out gradient */}
                  <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
                </div>

                {/* Sticky Footer Button */}
                <div className="flex justify-center px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
                  <Button
                    type="submit"
                    disabled={isSaving}
                    className="w-full text-[17px] font-semibold text-white px-7 py-3.5 h-auto rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-sm hover:shadow-md"
                  >
                    {isSaving ? ui.saving : ui.save}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

